import { makeStyles } from '@mui/styles';
import Accordion from 'apollo-react/components/Accordion';
import AccordionSummary from 'apollo-react/components/AccordionSummary';
import Checkbox from 'apollo-react/components/Checkbox';
import MenuItem from 'apollo-react/components/MenuItem';
import Select from 'apollo-react/components/Select';
import TextField from 'apollo-react/components/TextField';
import Typography from 'apollo-react/components/Typography';
import classNames from 'classnames';
import moment from 'moment/moment';
import React, { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import DateInput from './DateInput';
import DistributionTab from '../../components/DistributionTab';
import {
  DISTRIBUTION_FOR_DETAILS,
  DISTRIBUTION_TYPE_ACTOR,
} from '../../constants/SmartInsightConstants';
import { useTranslation } from '../../custom-hooks/useTranslation';
import { getModifiedDistributionRows } from '../../saved-search-designer/utils/common.utils';
import { getHasSupervisorRight } from '../../selectors/SmartInsightSelectors';
import { isTrue } from '../../utils/commonUtils';
import { DATE_FORMAT } from '../../utils/dateUtils';
import {
  addDistributionListRow,
  addDistributionListRows,
  deleteDistributionListRow,
  updateControlProperties,
  updateControlProperty,
  validateReport,
} from '../actions/report-designer.actions';
import RowField from '../components/RowField';
import { NOTIFICATION_FREQUENCY_OPTIONS } from '../constants/report-designer.constants';
import {
  getControlProperty,
  getDistributionListRows,
} from '../selectors/report-designer.selectors';

const useStyles = makeStyles({
  notificationContainer: {
    paddingLeft: 32,
    marginBottom: 20,
  },
  rowLabel: {
    width: 200,
    paddingTop: 8,
    marginRight: 20,
    alignItems: 'center',
  },
  field: {
    margin: 0,
    flex: 1,
  },
  select: {
    maxWidth: 300,
  },
});

const SSDistributionContent = ({ id }) => {
  const dispatch = useDispatch();
  const translateString = useTranslation();
  const classes = useStyles();

  const hasSupervisorRight = useSelector(getHasSupervisorRight);
  const distributionFor = useSelector((state) =>
    getControlProperty(state, { id, prop: 'distributionFor' })
  );
  const messageText = useSelector((state) =>
    getControlProperty(state, { id, prop: 'messageText' })
  );
  const notify = useSelector((state) => getControlProperty(state, { id, prop: 'notify' }));
  const frequency = useSelector((state) => getControlProperty(state, { id, prop: 'frequency' }));
  const startDate = useSelector((state) => getControlProperty(state, { id, prop: 'startDate' }));
  const distributionList = useSelector(getDistributionListRows);

  const [notificationMessage, setNotificationMessage] = useState(messageText);

  const showNotificationSection = [DISTRIBUTION_TYPE_ACTOR].includes(distributionFor);

  const onChangeDistributionValue = ({ target }) => {
    if (distributionFor === target.value) {
      return;
    }

    setNotificationMessage('');

    dispatch(
      updateControlProperties(id, [
        { propName: 'distributionFor', propValue: target.value },
        { propName: 'distributionList', propValue: [] },
        { propName: 'notify', propValue: false },
        { propName: 'frequency', propValue: 0 },
        { propName: 'startDate', propValue: '' },
        { propName: 'messageText', propValue: '' },
      ])
    );
  };

  const handleZoomSelectDistributionList = useCallback(
    ({ row, selection = [] }) => {
      if (selection.length) {
        const savedSearchDetails = DISTRIBUTION_FOR_DETAILS[distributionFor];

        dispatch(deleteDistributionListRow({ rowId: row.rowId }));

        const rows = getModifiedDistributionRows({
          rows: selection,
          mappingFields: savedSearchDetails.mappingFields,
          distributionFor,
        });

        dispatch(addDistributionListRows({ rows }));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [distributionFor]
  );

  const handleNotificationMessageChange = ({ target: { value } }) => {
    const newValue = value.trim();
    setNotificationMessage(newValue);

    if (messageText !== newValue) {
      dispatch(updateControlProperty(id, 'messageText', newValue));
    }
  };

  const handleAddNewDistributionRow = (data) => dispatch(addDistributionListRow(data));
  const handleDeleteNewDistributionRow = (data) => dispatch(deleteDistributionListRow(data));
  const handleValidateDistribution = () => dispatch(validateReport());

  const isViewOnly = !hasSupervisorRight;

  const notifyViewOnlyValue = isTrue(notify) ? 'Yes' : 'No';
  const startDateViewOnlyValue = isViewOnly && startDate && moment(startDate).format(DATE_FORMAT);
  const frequencyViewOnlyValue =
    isViewOnly && NOTIFICATION_FREQUENCY_OPTIONS.find(({ value }) => value === frequency).label;

  return (
    <>
      <Accordion defaultExpanded>
        <AccordionSummary>
          <Typography>{translateString('Report Distribution')}</Typography>
        </AccordionSummary>
        <DistributionTab
          distributionForValueStore={distributionFor}
          distributionListRows={distributionList}
          handleAddNewDistributionRow={handleAddNewDistributionRow}
          onChangeDistributionValue={onChangeDistributionValue}
          handleDeleteDistributionRow={handleDeleteNewDistributionRow}
          handleZoomSelectDistributionList={handleZoomSelectDistributionList}
          validateDistribution={handleValidateDistribution}
        />
      </Accordion>
      {showNotificationSection && (
        <Accordion defaultExpanded className={classes.accordionContainer}>
          <AccordionSummary>
            <Typography>{translateString('Report Notification')}</Typography>
          </AccordionSummary>
          <div className={classes.notificationContainer}>
            <RowField
              rowLabel="Notify People On Distribution List"
              isViewOnly={isViewOnly}
              viewOnlyValue={translateString(notifyViewOnlyValue)}
            >
              <Checkbox
                checked={notify}
                onChange={({ target: { checked } }) =>
                  dispatch(updateControlProperty(id, 'notify', checked))
                }
                aria-label={translateString('Notify People On Distribution List')}
              />
            </RowField>
            <RowField
              rowLabel="Frequency"
              isViewOnly={isViewOnly}
              viewOnlyValue={frequencyViewOnlyValue}
            >
              <Select
                value={frequency}
                onChange={({ target: { value } }) =>
                  dispatch(updateControlProperty(id, 'frequency', value))
                }
                className={classNames(classes.field, classes.select)}
                canDeselect={false}
              >
                {NOTIFICATION_FREQUENCY_OPTIONS.map(({ value, label }) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </RowField>
            <RowField
              rowLabel="Start Date"
              isViewOnly={isViewOnly}
              viewOnlyValue={startDateViewOnlyValue}
            >
              <DateInput
                id={id}
                controlType="startDate"
                className={classes.field}
                viewOnly={!hasSupervisorRight}
              />
            </RowField>
            <RowField
              rowLabel="Notification Message Text"
              isViewOnly={isViewOnly}
              viewOnlyValue={notificationMessage}
            >
              <TextField
                value={notificationMessage ?? ''}
                sizeAdjustable
                multiline
                fullWidth
                className={classes.field}
                onBlur={handleNotificationMessageChange}
                onChange={({ target: { value } }) => setNotificationMessage(value)}
                inputProps={{
                  rows: 2,
                  style: {
                    height: 'auto',
                    overflow: 'auto',
                    resize: 'none',
                  },
                  'aria-label': translateString('Notification Message Text'),
                }}
              />
            </RowField>
          </div>
        </Accordion>
      )}
    </>
  );
};

export default SSDistributionContent;
